import React, { Component } from "react";

import UserBaseUtils from "Utility/UserBase.js";
import AxiosWidgetChurch from "./Axios";
import ImageUploader from "./ImageUploader";

interface MessageState {
  type: "error" | "success" | "info" | null;
  text: string | null;
}

interface CreateEventState {
  textArea: string;
  textNameArea: string;
  username: any;
  isSubmitting: boolean;
  message: MessageState;
  selectedImage: string;
  selectedLogo: string;
}

interface QuestionData {
  body: string;
  churchName: string;
}

interface CreateEventProps {
  onClose: () => void;
  inputData: (question: QuestionData) => Promise<void>;
  churchName?: string;
}

// Placeholder for external data submission
const SendToSupabase = (text: string, username: string) => {
  console.log(`Sending to Supabase: "${text}" from user: ${username}`);
  // Simulate API delay
  return new Promise((resolve) => setTimeout(resolve, 500));
};

// Component is now typed with empty props ({}) and the defined state interface
class CreateEvent extends Component<CreateEventProps, CreateEventState> {
  // Initialize state using the CreatePrayerState interface
  state: CreateEventState = {
    textArea: "",
    textNameArea: "",
    username: UserBaseUtils.getUserName(),
    isSubmitting: false,
    message: { type: null, text: null },
    selectedImage: "",
    selectedLogo: "",
  };

  constructor(props: CreateEventProps) {
    super(props);
    // Bindings are necessary for class methods used as event handlers
    this.handleTextChange = this.handleTextChange.bind(this);
    this.handleTextNameChange = this.handleTextNameChange.bind(this);
    this.handleImageSelect = this.handleImageSelect.bind(this);
    this.handleLogoSelect = this.handleLogoSelect.bind(this);
    this.PostData = this.PostData.bind(this);
  }

  getUserName(): string {
    return this.state.username;
  }

  // Type the event as a change event from an HTMLTextAreaElement
  handleTextChange(event: React.ChangeEvent<HTMLTextAreaElement>): void {
    this.setState({
      textArea: event.target.value,
      message: { type: null, text: null }, // Clear messages on input
    });
  }

  handleTextNameChange(event: React.ChangeEvent<HTMLTextAreaElement>): void {
    // Convert input to lowercase automatically
    const lowercaseValue = event.target.value.toLowerCase();
    this.setState({
      textNameArea: lowercaseValue,
      message: { type: null, text: null }, // Clear messages on input
    });
  }

  handleImageSelect(imageData: string): void {
    this.setState({
      selectedImage: imageData,
      message: { type: null, text: null }, // Clear messages on input
    });
  }

  handleLogoSelect(imageData: string): void {
    this.setState({
      selectedLogo: imageData,
      message: { type: null, text: null },
    });
  }

  async PostData(): Promise<void> {
    const { textArea, textNameArea, selectedImage, selectedLogo } = this.state;
    const username: string = this.getUserName();
    const fileName = `${UserBaseUtils.getProfileUserName()}_${textNameArea.replace(
      /[^a-zA-Z0-9]/g,
      "_"
    )}_church.jpg`;
    var json = {
      body: textArea,
      user: UserBaseUtils.getProfileUserName(),
      churchName: textNameArea,
    };

    var jsonaxios: {
      name: string;
      introduction: string;
      personCreate: string;
      fileName: string;
      logoFileName?: string;
    } = {
      name: textNameArea,
      introduction: textArea,
      personCreate: UserBaseUtils.getProfileUserName(),
      fileName: fileName,
    };

    if (!textArea.trim() || !textNameArea.trim()) {
      this.setState({
        message: {
          type: "error",
          text: "Please enter both church name and introduction before submitting.",
        },
      });
      return;
    }

    this.setState({
      isSubmitting: true,
      message: { type: "info", text: "Submitting..." },
    });

    try {
      // Upload image first if one is selected
      let imageUrl = null;
      if (selectedImage) {
        this.setState({
          message: { type: "info", text: "Uploading image..." },
        });

        const uploadResult = await AxiosWidgetChurch.uploadChurchImage(
          selectedImage,
          textNameArea,
          UserBaseUtils.getProfileUserName(),
          fileName
        );

        if (uploadResult.error) {
          throw new Error(`Image upload failed: ${uploadResult.error}`);
        }

        imageUrl = uploadResult.imageUrl;
      }

      // Upload logo if provided (optional)
      if (selectedLogo) {
        this.setState({
          message: { type: "info", text: "Uploading logo..." },
        });

        const logoFileName = `${UserBaseUtils.getProfileUserName()}_${textNameArea.replace(
          /[^a-zA-Z0-9]/g,
          "_"
        )}_logo.svg`;

        const uploadLogoResult = await AxiosWidgetChurch.uploadChurchLogo(
          selectedLogo,
          textNameArea,
          UserBaseUtils.getProfileUserName(),
          logoFileName
        );

        if (uploadLogoResult.error) {
          throw new Error(`Logo upload failed: ${uploadLogoResult.error}`);
        }

        jsonaxios.logoFileName = logoFileName;
      }

      // Create church
      await AxiosWidgetChurch.createChurch(jsonaxios);
      this.props.inputData(json);
      this.setState({
        textArea: "",
        textNameArea: "",
        selectedImage: "",
        selectedLogo: "",
        message: {
          type: "success",
          text: "Your church has been created successfully!",
        },
      });
    } catch (error) {
      console.error("Submission failed:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to submit post. Please try again.";
      this.setState({
        message: {
          type: "error",
          text: errorMessage,
        },
      });
    } finally {
      this.setState({ isSubmitting: false });
    }

    this.props.onClose();
  }

  render() {
    const {
      textArea,
      isSubmitting,
      message,
      textNameArea,
      selectedImage,
      selectedLogo,
    } = this.state;

    // Determine message styling based on type
    let messageClasses = "";
    if (message.type === "error") {
      messageClasses = "bg-red-100 text-red-700 border-red-400";
    } else if (message.type === "success") {
      messageClasses = "bg-green-100 text-green-700 border-green-400";
    } else if (message.type === "info") {
      messageClasses = "bg-blue-100 text-blue-700 border-blue-400";
    }

    return (
      // Full-screen background and centering container
      <div
        className="flex w-full items-center justify-center p-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Content box for the form elements */}
        <div className="w-full max-w-lg space-y-5 rounded-xl bg-white p-6 shadow-2xl">
          <button
            onClick={this.props.onClose}
            className="absolute right-3 top-3 z-10 rounded-full p-2 text-white transition duration-150 ease-in-out hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close form"
          >
            {/* SVG icon for a crisp 'X' */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
          <h2 className="text-center text-3xl font-extrabold text-gray-800">
            Create Church
          </h2>

          {/* Message Display Area */}
          {message.text && (
            <div
              className={`rounded-lg border-l-4 p-3 text-sm font-medium ${messageClasses}`}
            >
              {message.text}
            </div>
          )}
          <textarea
            className="h-14 w-full resize-none rounded-lg border border-gray-300 p-4 shadow-inner transition duration-300 ease-in-out focus:outline-none focus:ring-4 focus:ring-blue-500/50"
            value={textNameArea}
            onChange={this.handleTextNameChange}
            placeholder="Name of your church..."
            disabled={isSubmitting}
          />
          <textarea
            className="h-48 w-full resize-none rounded-lg border border-gray-300 p-4 shadow-inner transition duration-300 ease-in-out focus:outline-none focus:ring-4 focus:ring-blue-500/50"
            value={textArea}
            onChange={this.handleTextChange}
            placeholder="Introduction of your church..."
            disabled={isSubmitting}
          />

          {/* Image Uploader */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Church Image
            </label>
            <ImageUploader
              onImageSelect={this.handleImageSelect}
              disabled={isSubmitting}
            />
          </div>

          {/* Church Logo Uploader (Optional) */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Church Logo
            </label>
            <ImageUploader
              onImageSelect={this.handleLogoSelect}
              accept="image/svg+xml"
              onlySvg
              disabled={isSubmitting}
            />
          </div>

          <button
            className="w-full transform rounded-lg bg-blue-600 py-3 font-extrabold text-white shadow-md transition duration-300 ease-in-out hover:scale-[1.01] hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={this.PostData}
            disabled={!textArea.trim() || !textNameArea.trim() || isSubmitting} // Disable if text or name is empty or submitting
          >
            {isSubmitting ? "Sending..." : "Submit"}
          </button>
        </div>
      </div>
    );
  }
}

export default CreateEvent;
